import UICard, {UICardFooter, UICardFooterButton, UICardHeading} from "@/components/Shared/Utils/UICard";
import {Box, Chip, Grid, Stack, Typography} from "@mui/material";
import {resolveCustomerServices} from "@/services/services.ts";
import {useMemo, useState} from "react";
import {Service} from "@/types/utils.ts";
import Icons from "@/features/Dashboard/Icons";
import {Link} from "react-router-dom";
import {useAppContext} from "@/contexts/AppContext.tsx";
import {grey} from "@mui/material/colors";
import Utils from "@/components/Shared/Utils";
import {useRecentServices} from "@/hooks/useRecentServices";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

// eslint-disable-next-line react-refresh/only-export-components
export function selfManagedServices(services: Service[]) {
    return services?.map((service: Service) => ({
        ...service,
        id: service.service_id,
        owned: true
    }))?.filter((service: Service) => service.service_type !== "Infra.Managed")
}

function ServiceDetail({service, lastAccessedAt}: { service: Service, lastAccessedAt?: string }) {
    const {theme, uiVisual} = useAppContext();

    const [focused, setFocused] = useState(false);
    const iconKey = service.provider_scope?.split(".").join("") || '';
    const Icon = Icons[iconKey];

    const sx = {
        classic: {
            cursor: 'pointer',
            display: 'block',
            p: 2,
            bgcolor: focused ? grey[100] : grey[50],
            border: `1px solid ${grey[200]}`,
            borderRadius: 0
        },
        modern: {
            cursor: 'pointer',
            display: 'block',
            p: 2,
            bgcolor: focused ? 'white' : '#F9FBFF',
            border: focused ? '3px solid #F9FBFF' : '3px solid white',
            boxShadow: '0px 4px 10px 0px #E0E0E04D',
            borderRadius: 2
        }
    }
    const chipStyles = {
        classic: {
            ml: 1,
            bgcolor: grey[300],
            fontSize: 12
        },
        modern: {
            ml: 1,
            bgcolor: "#134EA90F",
            fontSize: 12
        }
    }

    return (
        <Box component={Link} to={`/services/${service.service_uuid}`} sx={sx[uiVisual]}
             onMouseOver={() => setFocused(true)} onMouseLeave={() => setFocused(false)}>
            <Stack direction="row">
                {Icon && (
                    <Icon focused={focused} mr={2} sx={{filter: theme === 'dark' ? 'invert(1)' : 'invert(0)'}}/>
                )}
                <Box>
                    <Typography mb={0.5} fontSize={14} fontWeight={500}>{service.service_name}</Typography>
                    {lastAccessedAt && (
                        <Typography fontSize={12} color="text.secondary" sx={{mb: 0.5}}>
                            Last accessed {dayjs(lastAccessedAt).fromNow()}
                        </Typography>
                    )}
                    <Stack direction="row">
                        <Chip label={service.service_type} size="small" sx={{...chipStyles[uiVisual], ml: 0}}/>
                        {service.default && (
                            <Chip label="Default" size="small" sx={chipStyles[uiVisual]}/>
                        )}
                        {service.type === "shared" && (
                            <Chip label="Teammate Resource Share" size="small" sx={chipStyles[uiVisual]}/>
                        )}
                    </Stack>
                </Box>
            </Stack>
        </Box>
    )
}

export default function MainRecentServices() {
    const {data} = resolveCustomerServices();
    const services = useMemo(() => data?.data?.services || [], [data]);
    const {displayServices} = useRecentServices();
    
    // Filter out Infra.Managed services from recent services
    const recentServicesList = useMemo(() => {
        return displayServices
            .filter(item => item.service.service_type !== "Infra.Managed")
            .map(item => ({service: item.service, lastAccessedAt: item.lastAccessedAt}));
    }, [displayServices]);
    
    // Fallback to first 3 services if no recent services
    const fallbackServices = useMemo(() => selfManagedServices(services)?.slice(0, 3), [services]);
    const hasRecentServices = recentServicesList.length > 0;
    const rows = hasRecentServices ? recentServicesList : fallbackServices.map(service => ({service, lastAccessedAt: undefined}));

    return (
        <UICard>
            <UICardHeading title="Recent Services"/>
            <Box p={2}>
                {rows.length !== 0 ? (
                    <Grid container spacing={2}>
                        {rows?.map((item) => {
                            return (
                                <Grid item md={4} xs={12} key={item.service.service_id}>
                                    <ServiceDetail service={item.service} lastAccessedAt={item.lastAccessedAt}/>
                                </Grid>
                            )
                        })}
                    </Grid>
                ) : (
                    <Utils.LayoutUtils.MainInformationFact variant="alert">
                        It seems there's an issue accessing your services. Please try refreshing the page or contact our support team for further help.
                    </Utils.LayoutUtils.MainInformationFact>
                )}
            </Box>
            <UICardFooter>
                <UICardFooterButton pathname="/services" text={`Manage All Services (${services?.length})`}/>
            </UICardFooter>
        </UICard>
    )
}